<template>
  <!-- The bar is intentionally lightweight visually — no glass-panel
       background, just a row of filter chips. Pages give it spacing
       above/below themselves; the bar fills the available width. -->
  <div class="flex items-center flex-wrap gap-2">
    <!-- Date range — always first when enabled. -->
    <DateRangeFilter
      v-if="filters.enabled.dateRange"
      v-model="filters.dateRange"
    />

    <!-- Multi-select filters render in a fixed order so layouts stay
         predictable across pages. Pages opt-in via the composable's
         `enabled` config. -->
    <MultiSelectFilter
      v-if="filters.enabled.warehouses"
      label="Warehouse"
      :options="warehouseOptions"
      v-model="filters.warehouses"
    />
    <MultiSelectFilter
      v-if="filters.enabled.users"
      label="User"
      :options="userOptions"
      v-model="filters.users"
    />
    <MultiSelectFilter
      v-if="filters.enabled.roles"
      label="Role"
      :options="roleOptions"
      v-model="filters.roles"
    />

    <!-- Reset — only shown when at least one filter is non-default. The
         bar exposes a `dirty` flag computed by the composable; pages
         pass it through. -->
    <button
      v-if="dirty"
      type="button"
      @click="$emit('reset')"
      class="ml-auto px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
    >
      Clear filters
    </button>
  </div>
</template>

<script setup lang="ts">
import DateRangeFilter from '~/components/dashboard/filters/DateRangeFilter.vue'
import MultiSelectFilter from '~/components/dashboard/filters/MultiSelectFilter.vue'
import type { FilterState } from '~/composables/useDashboardFilters'

defineProps<{
  /** Reactive filter state from `useDashboardFilters`. The bar mutates
   *  the state directly via v-model on each filter component. */
  filters: FilterState
  /** Whether any filter is currently non-default — drives the
   *  Clear filters button's visibility. */
  dirty: boolean
  /** Optional dropdown sources for multi-select filters. Pages that
   *  enable warehouses/users/roles pass the candidate lists in.
   *  Omitting them yields empty dropdowns (filter still shows but
   *  the user has nothing to pick). */
  warehouseOptions?: string[]
  userOptions?: string[]
  roleOptions?: string[]
}>()

defineEmits<{ (e: 'reset'): void }>()
</script>
